import React from 'react';
import {AlertCircle} from 'lucide-react';

/**
 * Preparatory stage peer assessment component with emoji-based UI (READ-ONLY)
 * Designed to match the UI in the provided image
 */
const PreparatoryPeerAssessment = ({submission}) => {
    // If no peer assessment data is available, show placeholder
    if (!submission || !submission.peer_assessment) {
        return (
            <div className="bg-white font-[poppins] shadow-sm rounded-lg overflow-hidden mb-6 border border-orange-200">
                <div className="px-4 py-3 bg-orange-500 text-white flex justify-between items-center">
                    <div>
                        <h2 className="text-lg font-medium">Peer Feedback</h2>
                        <p className="mt-1 max-w-2xl text-sm">
                            Another student's assessment of your work
                        </p>
                    </div>
                    <div className="bg-orange-600 text-white text-sm font-medium py-2 px-5 rounded-full">
                        Peer 1
                    </div>
                </div>
                <div className="p-6 text-center">
                    <AlertCircle className="h-12 w-12 text-orange-200 mx-auto mb-3"/>
                    <p className="text-gray-500">No peer assessment has been submitted yet.</p>
                </div>
            </div>
        );
    }

    // Helper function to determine which response was selected
    const isSelected = (field, value) => {
        const response = submission.peer_assessment[field];
        return response === value;
    };

    return (
        <div className="bg-white font-[poppins] shadow-sm rounded-lg overflow-hidden mb-6 border border-orange-200">
            <div className="px-4 py-3 bg-orange-500 text-white flex justify-between items-center">
                <div>
                    <h2 className="text-lg font-medium">Peer Feedback</h2>
                    <p className="mt-1 max-w-2xl text-sm">
                        Circle the most appropriate option for each sentence
                    </p>
                </div>
                <div className="bg-orange-600 text-white text-sm font-medium py-2 px-5 rounded-full">
                    Peer 1
                </div>
            </div>

            <div className="p-4">
                <div className="bg-orange-50 rounded-md p-4 mb-4">
                    <div className="grid grid-cols-2 gap-4">
                        <div className="bg-purple-100 rounded-md p-3 border border-purple-200">
                            <p className="text-sm font-medium text-gray-700">My name:</p>
                            <p className="text-sm mt-1">{submission.peer_assessment.name || "Not provided"}</p>
                        </div>
                        <div className="bg-green-100 rounded-md p-3 border border-green-200">
                            <p className="text-sm font-medium text-gray-700">My friend's name:</p>
                            <p className="text-sm mt-1">{submission.peer_assessment.friend_name || "Not provided"}</p>
                        </div>
                    </div>
                </div>

                <table className="min-w-full border border-orange-200">
                    <thead>
                    <tr className="bg-orange-500 text-white">
                        <th className="px-4 py-2 text-left border-r border-orange-300 w-1/2">Statement</th>
                        <th className="px-4 py-2 text-center border-r border-orange-300">Yes</th>
                        <th className="px-4 py-2 text-center border-r border-orange-300">Sometimes</th>
                        <th className="px-4 py-2 text-center border-r border-orange-300">No</th>
                        <th className="px-4 py-2 text-center">Not sure</th>
                    </tr>
                    </thead>
                    <tbody>
                    {/* Question 1 */}
                    <tr className="border-b border-orange-200 bg-orange-50">
                        <td className="px-4 py-4 text-sm text-gray-900 border-r border-orange-200">
                            1. My friend can talk about how he/she feels, e.g., happy, confident, upset, or angry.
                        </td>
                        <td className={`px-4 py-4 text-center border-r border-orange-200 ${isSelected('can_express_emotions', 'Yes') ? 'bg-orange-100' : ''}`}>
                            <div className="flex flex-col items-center">
                                <div
                                    className={`w-10 h-10 rounded-full flex items-center justify-center ${isSelected('can_express_emotions', 'Yes') ? 'ring-2 ring-orange-500' : ''}`}>
                                    <span className="text-2xl">😊👍</span>
                                </div>
                            </div>
                        </td>
                        <td className={`px-4 py-4 text-center border-r border-orange-200 ${isSelected('can_express_emotions', 'Sometimes') ? 'bg-orange-100' : ''}`}>
                            <div className="flex flex-col items-center">
                                <div
                                    className={`w-10 h-10 rounded-full flex items-center justify-center ${isSelected('can_express_emotions', 'Sometimes') ? 'ring-2 ring-orange-500' : ''}`}>
                                    <span className="text-2xl">😐👈</span>
                                </div>
                            </div>
                        </td>
                        <td className={`px-4 py-4 text-center border-r border-orange-200 ${isSelected('can_express_emotions', 'No') ? 'bg-orange-100' : ''}`}>
                            <div className="flex flex-col items-center">
                                <div
                                    className={`w-10 h-10 rounded-full flex items-center justify-center ${isSelected('can_express_emotions', 'No') ? 'ring-2 ring-orange-500' : ''}`}>
                                    <span className="text-2xl">😔👎</span>
                                </div>
                            </div>
                        </td>
                        <td className={`px-4 py-4 text-center ${isSelected('can_express_emotions', 'Not sure') ? 'bg-orange-100' : ''}`}>
                            <div className="flex flex-col items-center">
                                <div
                                    className={`w-10 h-10 rounded-full flex items-center justify-center ${isSelected('can_express_emotions', 'Not sure') ? 'ring-2 ring-orange-500' : ''}`}>
                                    <span className="text-2xl">🤔</span>
                                </div>
                            </div>
                        </td>
                    </tr>

                    {/* Question 2 */}
                    <tr className="border-b border-orange-200">
                        <td className="px-4 py-4 text-sm text-gray-900 border-r border-orange-200">
                            2. My friend can calm himself/herself down during difficult situations.
                        </td>
                        <td className={`px-4 py-4 text-center border-r border-orange-200 ${isSelected('can_calm_down', 'Yes') ? 'bg-orange-100' : ''}`}>
                            <div className="flex flex-col items-center">
                                <div
                                    className={`w-10 h-10 rounded-full flex items-center justify-center ${isSelected('can_calm_down', 'Yes') ? 'ring-2 ring-orange-500' : ''}`}>
                                    <span className="text-2xl">😊👍</span>
                                </div>
                            </div>
                        </td>
                        <td className={`px-4 py-4 text-center border-r border-orange-200 ${isSelected('can_calm_down', 'Sometimes') ? 'bg-orange-100' : ''}`}>
                            <div className="flex flex-col items-center">
                                <div
                                    className={`w-10 h-10 rounded-full flex items-center justify-center ${isSelected('can_calm_down', 'Sometimes') ? 'ring-2 ring-orange-500' : ''}`}>
                                    <span className="text-2xl">😐👈</span>
                                </div>
                            </div>
                        </td>
                        <td className={`px-4 py-4 text-center border-r border-orange-200 ${isSelected('can_calm_down', 'No') ? 'bg-orange-100' : ''}`}>
                            <div className="flex flex-col items-center">
                                <div
                                    className={`w-10 h-10 rounded-full flex items-center justify-center ${isSelected('can_calm_down', 'No') ? 'ring-2 ring-orange-500' : ''}`}>
                                    <span className="text-2xl">😔👎</span>
                                </div>
                            </div>
                        </td>
                        <td className={`px-4 py-4 text-center ${isSelected('can_calm_down', 'Not sure') ? 'bg-orange-100' : ''}`}>
                            <div className="flex flex-col items-center">
                                <div
                                    className={`w-10 h-10 rounded-full flex items-center justify-center ${isSelected('can_calm_down', 'Not sure') ? 'ring-2 ring-orange-500' : ''}`}>
                                    <span className="text-2xl">🤔</span>
                                </div>
                            </div>
                        </td>
                    </tr>

                    {/* Question 3 */}
                    <tr className="border-b border-orange-200 bg-orange-50">
                        <td className="px-4 py-4 text-sm text-gray-900 border-r border-orange-200">
                            3. My friend can understand how his/her friends feel.
                        </td>
                        <td className={`px-4 py-4 text-center border-r border-orange-200 ${isSelected('can_understand_others', 'Yes') ? 'bg-orange-100' : ''}`}>
                            <div className="flex flex-col items-center">
                                <div
                                    className={`w-10 h-10 rounded-full flex items-center justify-center ${isSelected('can_understand_others', 'Yes') ? 'ring-2 ring-orange-500' : ''}`}>
                                    <span className="text-2xl">😊👍</span>
                                </div>
                            </div>
                        </td>
                        <td className={`px-4 py-4 text-center border-r border-orange-200 ${isSelected('can_understand_others', 'Sometimes') ? 'bg-orange-100' : ''}`}>
                            <div className="flex flex-col items-center">
                                <div
                                    className={`w-10 h-10 rounded-full flex items-center justify-center ${isSelected('can_understand_others', 'Sometimes') ? 'ring-2 ring-orange-500' : ''}`}>
                                    <span className="text-2xl">😐👈</span>
                                </div>
                            </div>
                        </td>
                        <td className={`px-4 py-4 text-center border-r border-orange-200 ${isSelected('can_understand_others', 'No') ? 'bg-orange-100' : ''}`}>
                            <div className="flex flex-col items-center">
                                <div
                                    className={`w-10 h-10 rounded-full flex items-center justify-center ${isSelected('can_understand_others', 'No') ? 'ring-2 ring-orange-500' : ''}`}>
                                    <span className="text-2xl">😔👎</span>
                                </div>
                            </div>
                        </td>
                        <td className={`px-4 py-4 text-center ${isSelected('can_understand_others', 'Not sure') ? 'bg-orange-100' : ''}`}>
                            <div className="flex flex-col items-center">
                                <div
                                    className={`w-10 h-10 rounded-full flex items-center justify-center ${isSelected('can_understand_others', 'Not sure') ? 'ring-2 ring-orange-500' : ''}`}>
                                    <span className="text-2xl">🤔</span>
                                </div>
                            </div>
                        </td>
                    </tr>

                    {/* Question 4 */}
                    <tr className="border-b border-orange-200">
                        <td className="px-4 py-4 text-sm text-gray-900 border-r border-orange-200">
                            4. My friend respects everyone's opinions.
                        </td>
                        <td className={`px-4 py-4 text-center border-r border-orange-200 ${isSelected('respects_opinions', 'Yes') ? 'bg-orange-100' : ''}`}>
                            <div className="flex flex-col items-center">
                                <div
                                    className={`w-10 h-10 rounded-full flex items-center justify-center ${isSelected('respects_opinions', 'Yes') ? 'ring-2 ring-orange-500' : ''}`}>
                                    <span className="text-2xl">😊👍</span>
                                </div>
                            </div>
                        </td>
                        <td className={`px-4 py-4 text-center border-r border-orange-200 ${isSelected('respects_opinions', 'Sometimes') ? 'bg-orange-100' : ''}`}>
                            <div className="flex flex-col items-center">
                                <div
                                    className={`w-10 h-10 rounded-full flex items-center justify-center ${isSelected('respects_opinions', 'Sometimes') ? 'ring-2 ring-orange-500' : ''}`}>
                                    <span className="text-2xl">😐👈</span>
                                </div>
                            </div>
                        </td>
                        <td className={`px-4 py-4 text-center border-r border-orange-200 ${isSelected('respects_opinions', 'No') ? 'bg-orange-100' : ''}`}>
                            <div className="flex flex-col items-center">
                                <div
                                    className={`w-10 h-10 rounded-full flex items-center justify-center ${isSelected('respects_opinions', 'No') ? 'ring-2 ring-orange-500' : ''}`}>
                                    <span className="text-2xl">😔👎</span>
                                </div>
                            </div>
                        </td>
                        <td className={`px-4 py-4 text-center ${isSelected('respects_opinions', 'Not sure') ? 'bg-orange-100' : ''}`}>
                            <div className="flex flex-col items-center">
                                <div
                                    className={`w-10 h-10 rounded-full flex items-center justify-center ${isSelected('respects_opinions', 'Not sure') ? 'ring-2 ring-orange-500' : ''}`}>
                                    <span className="text-2xl">🤔</span>
                                </div>
                            </div>
                        </td>
                    </tr>

                    {/* Question 5 */}
                    <tr className="border-b border-orange-200 bg-orange-50">
                        <td className="px-4 py-4 text-sm text-gray-900 border-r border-orange-200">
                            5. My friend can help others make up after a fight.
                        </td>
                        <td className={`px-4 py-4 text-center border-r border-orange-200 ${isSelected('helps_after_fight', 'Yes') ? 'bg-orange-100' : ''}`}>
                            <div className="flex flex-col items-center">
                                <div
                                    className={`w-10 h-10 rounded-full flex items-center justify-center ${isSelected('helps_after_fight', 'Yes') ? 'ring-2 ring-orange-500' : ''}`}>
                                    <span className="text-2xl">😊👍</span>
                                </div>
                            </div>
                        </td>
                        <td className={`px-4 py-4 text-center border-r border-orange-200 ${isSelected('helps_after_fight', 'Sometimes') ? 'bg-orange-100' : ''}`}>
                            <div className="flex flex-col items-center">
                                <div
                                    className={`w-10 h-10 rounded-full flex items-center justify-center ${isSelected('helps_after_fight', 'Sometimes') ? 'ring-2 ring-orange-500' : ''}`}>
                                    <span className="text-2xl">😐👈</span>
                                </div>
                            </div>
                        </td>
                        <td className={`px-4 py-4 text-center border-r border-orange-200 ${isSelected('helps_after_fight', 'No') ? 'bg-orange-100' : ''}`}>
                            <div className="flex flex-col items-center">
                                <div
                                    className={`w-10 h-10 rounded-full flex items-center justify-center ${isSelected('helps_after_fight', 'No') ? 'ring-2 ring-orange-500' : ''}`}>
                                    <span className="text-2xl">😔👎</span>
                                </div>
                            </div>
                        </td>
                        <td className={`px-4 py-4 text-center ${isSelected('helps_after_fight', 'Not sure') ? 'bg-orange-100' : ''}`}>
                            <div className="flex flex-col items-center">
                                <div
                                    className={`w-10 h-10 rounded-full flex items-center justify-center ${isSelected('helps_after_fight', 'Not sure') ? 'ring-2 ring-orange-500' : ''}`}>
                                    <span className="text-2xl">🤔</span>
                                </div>
                            </div>
                        </td>
                    </tr>

                    {/* Question 6 */}
                    <tr className="border-b border-orange-200">
                        <td className="px-4 py-4 text-sm text-gray-900 border-r border-orange-200">
                            6. When someone is sad, my friend can make them feel better.
                        </td>
                        <td className={`px-4 py-4 text-center border-r border-orange-200 ${isSelected('comforts_when_sad', 'Yes') ? 'bg-orange-100' : ''}`}>
                            <div className="flex flex-col items-center">
                                <div
                                    className={`w-10 h-10 rounded-full flex items-center justify-center ${isSelected('comforts_when_sad', 'Yes') ? 'ring-2 ring-orange-500' : ''}`}>
                                    <span className="text-2xl">😊👍</span>
                                </div>
                            </div>
                        </td>
                        <td className={`px-4 py-4 text-center border-r border-orange-200 ${isSelected('comforts_when_sad', 'Sometimes') ? 'bg-orange-100' : ''}`}>
                            <div className="flex flex-col items-center">
                                <div
                                    className={`w-10 h-10 rounded-full flex items-center justify-center ${isSelected('comforts_when_sad', 'Sometimes') ? 'ring-2 ring-orange-500' : ''}`}>
                                    <span className="text-2xl">😐👈</span>
                                </div>
                            </div>
                        </td>
                        <td className={`px-4 py-4 text-center border-r border-orange-200 ${isSelected('comforts_when_sad', 'No') ? 'bg-orange-100' : ''}`}>
                            <div className="flex flex-col items-center">
                                <div
                                    className={`w-10 h-10 rounded-full flex items-center justify-center ${isSelected('comforts_when_sad', 'No') ? 'ring-2 ring-orange-500' : ''}`}>
                                    <span className="text-2xl">😔👎</span>
                                </div>
                            </div>
                        </td>
                        <td className={`px-4 py-4 text-center ${isSelected('comforts_when_sad', 'Not sure') ? 'bg-orange-100' : ''}`}>
                            <div className="flex flex-col items-center">
                                <div
                                    className={`w-10 h-10 rounded-full flex items-center justify-center ${isSelected('comforts_when_sad', 'Not sure') ? 'ring-2 ring-orange-500' : ''}`}>
                                    <span className="text-2xl">🤔</span>
                                </div>
                            </div>
                        </td>
                    </tr>
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default PreparatoryPeerAssessment;